import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getLocationBySlug, getLocations } from '../../lib/sanity';
import Container from '../../components/shared/Container';
const MapboxMap = lazy(() => import('../../components/shared/MapboxMap'));
import Button from '../../components/shared/Button';
import LocationCard from '../../components/shared/LocationCard';
import LocationCardSkeleton from '../../components/shared/LocationCardSkeleton';
import LocationPageSkeleton from '../../components/shared/LocationPageSkeleton';
import SEO from '../../components/shared/SEO';
import StructuredData from '../../components/shared/StructuredData';
import { PortableText } from '@portabletext/react';
import { useLocation } from '../../context/LocationContext';
import { useWaitlist } from '../../context/WaitlistContext';
import { useOrderConfirmation } from '../../context/OrderConfirmationContext';
import { trackLocationView, trackOnlineOrderClick, trackReservationClick, trackPhoneClick } from '../../utils/gtm';
import { generateRestaurantSchema, generateBreadcrumbSchema } from '../../utils/schema';
import { getSanityImageProps } from '../../utils/sanityImage';
import { sortLocationsByDefault, calculateDistance } from '../../utils/locationSorting';
import { DIRECT_LINK_SLUGS } from '../../utils/waitlist';

export default function LocationPage() {
  const { slug } = useParams();
  const { selectedLocation: globalSelectedLocation } = useLocation();
  const { setWaitlistUrlAndOpenModal } = useWaitlist();
  const { confirmOrder } = useOrderConfirmation();
  const [location, setLocation] = useState(null);
  const [nearbyLocations, setNearbyLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNearbySection, setShowNearbySection] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    async function fetchLocationData() {
      try {
        setLoading(true);
        // Fetch the current location
        const locationData = await getLocationBySlug(slug);
        
        if (!locationData) {
          setError('Location not found');
          setLoading(false);
          return;
        }
        
        setLocation(locationData);
        
        // Track location page view
        trackLocationView(locationData.name, locationData.slug, locationData.region);
        
        // Fetch all locations to find nearby ones
        const allLocations = await getLocations();
        
        if (allLocations?.length > 0) {
          const region = locationData.region || '';
          
          // Handle different regions differently
          if (region === 'new-york') {
            // Hide nearby section for NY locations
            setShowNearbySection(false);
          } else if (region === 'indiana') {
            // For Indiana, show other Indiana locations using default sorting
            const indianaLocations = allLocations
              .filter(loc => loc._id !== locationData._id && loc.region === 'indiana');
            const sortedInLocations = sortLocationsByDefault(indianaLocations).slice(0, 2);
              
            setNearbyLocations(sortedInLocations);
            setShowNearbySection(sortedInLocations.length > 0);
          } else {
            // For Washington or other regions, show 3 closest locations
            if (locationData.coordinates) {
              let locationsWithDistance = allLocations
                .filter(loc => loc._id !== locationData._id && loc.coordinates)
                .map(loc => {
                  const distance = calculateDistance(
                    locationData.coordinates.lat,
                    locationData.coordinates.lng,
                    loc.coordinates.lat,
                    loc.coordinates.lng
                  );
                  return { ...loc, distance };
                })
                .sort((a, b) => a.distance - b.distance);
              
              // If there's a globally selected location, move it to the front
              if (globalSelectedLocation && globalSelectedLocation._id !== locationData._id) {
                const selectedIndex = locationsWithDistance.findIndex(loc => loc._id === globalSelectedLocation._id);
                if (selectedIndex > 0) {
                  const selectedLoc = locationsWithDistance.splice(selectedIndex, 1)[0];
                  locationsWithDistance.unshift(selectedLoc);
                }
              }
              
              // Take the first 3
              locationsWithDistance = locationsWithDistance.slice(0, 3);
                
              setNearbyLocations(locationsWithDistance);
              setShowNearbySection(locationsWithDistance.length > 0);
            } else {
              // If no coordinates, show locations from same region using default sorting
              const sameRegionLocations = allLocations
                .filter(loc => loc._id !== locationData._id && loc.region === region);
              const sortedRegionLocations = sortLocationsByDefault(sameRegionLocations).slice(0, 3);
                
              setNearbyLocations(sortedRegionLocations);
              setShowNearbySection(sortedRegionLocations.length > 0);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching location:', err);
        setError('Failed to load location data');
      } finally {
        setLoading(false);
      }
    }

    fetchLocationData();
  }, [slug]);

  if (loading) {
    return <LocationPageSkeleton />;
  }

  if (error || !location) {
    return (
      <Container className="py-12">
        <div className="flex flex-col justify-center items-center min-h-[400px]">
          <h1 className="text-3xl font-bold text-brand-navy mb-4 font-heading">Location Not Found</h1>
          <p className="text-gray-600">We couldn't find the location you're looking for.</p>
        </div>
      </Container>
    );
  }

  // Format location name for display
  const locationName = location.name || "Bothell";
  
  // Build rich description with address details
  const addressPart = location.address 
    ? `Located at ${location.address.street}, ${location.address.city}, ${location.address.state} ${location.address.zipCode}.` 
    : '';
  const descriptionText = location.description && typeof location.description === 'string' && location.description.length > 0
    ? `${location.description.substring(0, 120)}${location.description.length > 120 ? '...' : ''}` 
    : `Visit The Original Pancake House in ${locationName} for fresh, made-from-scratch breakfast and brunch.`;

  return (
    <>
      <SEO 
        title={`${locationName} | The Original Pancake House - Breakfast & Brunch Restaurant`}
        description={`${descriptionText} ${addressPart} Order online, join our waitlist, or visit us today for the best breakfast in ${locationName}.`}
        openGraph={{
          type: 'business.business',
          image: location.featuredImage || "/oph-social-share3.jpg"
        }}
      />
      
      {/* Structured Data for SEO */}
      <StructuredData data={[
        generateRestaurantSchema(location),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Locations', url: '/locations' },
          { name: locationName, url: `/locations/${slug}` }
        ])
      ]} />
      
      {/* Tan background header section with 8rem top padding */}
      <div className="bg-brand-cream pt-32 pb-32">
        <Container>
          <div className="text-left">
            <div className="flex items-center text-xs text-gray-700 mb-3">
              <Link to="/" className="hover:text-brand-orange transition-colors">Home</Link>
              <span className="mx-2">/</span>
              <Link to="/locations" className="hover:text-brand-orange transition-colors">Locations</Link>
              <span className="mx-2">/</span>
              <span className="text-brand-navy">{locationName}</span>
            </div>
            <h1 className="text-5xl font-bold text-brand-navy font-heading">{locationName}</h1>
          </div>
        </Container>
      </div>

      {/* Main content with overlapping image */}
      <div className="bg-white">
        <Container className="-mt-24">
          {/* Featured Image - Mobile Only - Full bleed with overlap */}
          <div className="lg:hidden -mt-16 mb-8 -mx-6">
            <img 
              {...getSanityImageProps(location.featuredImage || "/public/images/hero/blueberry-pancakes-original-pancake-house.jpg", 'locationDetail')}
              alt={locationName} 
              className="w-full h-auto shadow-lg object-cover"
              style={{ maxHeight: '400px' }}
              loading="lazy"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - About Content */}
            <div className="bg-white rounded-lg py-6 lg:p-6">
              <h2 className="text-2xl font-bold text-brand-navy mb-4 font-heading">About This Location</h2>
              
              {/* Additional Information Tags */}
              {location.additionalInformation && (location.additionalInformation.patioSeating || location.additionalInformation.privateRooms) && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {location.additionalInformation.patioSeating && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-brand-cream text-brand-navy border border-brand-navy/20">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      Patio Seating Available (seasonal)
                    </span>
                  )}
                  {location.additionalInformation.privateRooms && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-brand-cream text-brand-navy border border-brand-navy/20">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Private Rooms Available
                    </span>
                  )}
                </div>
              )}

              <div className="space-y-4 text-gray-700">
                {location.description ? (
                  <div className="prose prose-lg max-w-none location-description">
                    <PortableText value={location.description} />
                  </div>
                ) : (
                  <>
                    <p>
                      The Original Pancake House in {locationName} is the go-to spot for breakfast and brunch 
                      lovers in the North Seattle area. Known for its signature Dutch Baby, fluffy omelets, and 
                      freshly made buttermilk pancakes, this local favorite brings classic comfort food to the 
                      heart of {locationName}.
                    </p>
                    <p>
                      Conveniently located near downtown and easily accessible from nearby 
                      neighborhoods like Canyon Park, Mill Creek, and Woodinville, the {locationName} Original Pancake 
                      House is a perfect choice for families, friends, and anyone looking for a hearty start to 
                      their day. With its welcoming atmosphere and friendly service, it's no surprise this 
                      location has become a community staple.
                    </p>
                    <p>
                      Whether you're craving a stack of pancakes topped with seasonal fruit, a savory bacon 
                      and egg breakfast, or a freshly brewed cup of coffee, the {locationName} Original Pancake House 
                      delivers quality and tradition every time. Guests love the combination of generous 
                      portions and consistent flavors, making it a must-visit breakfast restaurant in {locationName}. If 
                      you're searching for "best breakfast near me" in {locationName}, or need a great brunch spot 
                      before exploring nearby parks and wineries, the Original Pancake House is the place to go.
                    </p>
                  </>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4 mt-6">
                {location.onlineOrderingUrl && (
                  <Button 
                    onClick={() => {
                      trackOnlineOrderClick(location.name, 'location_page');
                      confirmOrder(location.onlineOrderingUrl, location.name);
                    }}
                    variant="solid" 
                    color="navy"
                    className="text-sm py-2 px-5"
                    icon={
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    }
                  >
                    Order Online
                  </Button>
                )}
                {location.onlineReservationsUrl && (
                  (isMobile || DIRECT_LINK_SLUGS.includes(slug)) ? (
                    <Button 
                      as="a"
                      href={location.onlineReservationsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackReservationClick(location.name, 'location_page')}
                      variant="outline" 
                      color="navy"
                      className="text-sm py-2 px-5"
                      icon={
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      }
                    >
                      Join the waitlist
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => {
                        trackReservationClick(location.name, 'location_page');
                        setWaitlistUrlAndOpenModal(location.onlineReservationsUrl);
                      }}
                      variant="outline" 
                      color="navy"
                      className="text-sm py-2 px-5"
                      icon={
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      }
                    >
                      Join the waitlist
                    </Button>
                  )
                )}
              </div>
            </div>

            {/* Right Column - Image (Desktop Only) */}
            <div className="hidden lg:block lg:-mt-24">
              <img 
                {...getSanityImageProps(location.featuredImage || "/public/images/hero/blueberry-pancakes-original-pancake-house.jpg", 'locationDetail')}
                alt={locationName} 
                className="w-full h-auto rounded-lg shadow-lg object-cover"
                style={{ maxHeight: '500px' }}
                loading="lazy"
              />
            </div>
          </div>
        </Container>
      </div>

      {/* Map Section with Side Card */}
      <div className="relative py-16 pb-8 lg:pb-32 bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
          {/* Map - Takes 2/3 of the width */}
          <div className="lg:col-span-2 aspect-square lg:aspect-auto lg:h-[600px]">
            <Suspense fallback={
              <div className="h-full w-full flex items-center justify-center bg-gray-100">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-navy"></div>
              </div>
            }>
              <MapboxMap 
              locations={[{
                _id: location._id || "location-id",
                name: locationName,
                coordinates: location.coordinates || { lat: 47.76, lng: -122.20 },
                address: location.address || {
                  street: "19704 201st Pl SE",
                  city: "Bothell",
                  state: "WA",
                  zipCode: "98012"
                },
                slug: location.slug || "bothell"
              }]} 
              height="100%"
              width="100%"
              initialViewState={{
                longitude: location.coordinates?.lng || -122.20,
                latitude: location.coordinates?.lat || 47.76,
                zoom: 14
              }}
              />
            </Suspense>
          </div>
          
          {/* Contact Card - Takes 1/3 of the width and is vertically centered */}
          <div className="lg:col-span-1 bg-gray-100 flex items-center">
            <div className="p-6 lg:p-8 w-full">
              {/* Where to Find Us */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-brand-navy" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-bold text-brand-navy font-heading">Where to Find Us</h2>
                </div>
                <p className="ml-9 text-gray-700">
                  {location.address?.street || "19704 201st Pl SE"}<br />
                  {location.address?.city || "Bothell"}, {location.address?.state || "WA"} {location.address?.zipCode || "98012"}
                </p>
              </div>

              {/* How to Reach Us */}
              {location.phoneNumber && (
                <div className="mb-8">
                  <div className="flex items-center mb-4">
                    <div className="mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-brand-navy" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <h2 className="text-lg font-bold text-brand-navy font-heading">How to Reach Us</h2>
                  </div>
                  <p className="ml-9 text-gray-700">
                    <a 
                      href={`tel:${location.phoneNumber.replace(/\D/g, '')}`}
                      onClick={() => trackPhoneClick(location.name, location.phoneNumber, 'location_page')}
                      className="hover:text-brand-navy hover:underline"
                    >
                      {location.phoneNumber}
                    </a>
                  </p>
                </div>
              )}

              {/* Hours of Operation */}
              <div>
                <div className="flex items-center mb-4">
                  <div className="mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-brand-navy" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-bold text-brand-navy font-heading">Hours of Operation</h2>
                </div>
                <div className="ml-9 space-y-1 text-sm text-gray-700">
                  {location.hoursOfOperation?.map((hours, index) => (
                    <div key={index} className="flex justify-between">
                      <span>{hours.day.toUpperCase()}</span>
                      <span className="ml-4">
                        {hours.isClosed ? 'CLOSED' : `${hours.openTime} - ${hours.closeTime}`}
                      </span>
                    </div>
                  )) || (
                    <>
                      <div className="flex justify-between"><span>SUNDAY</span><span className="ml-4">7AM - 3PM</span></div>
                      <div className="flex justify-between"><span>MONDAY</span><span className="ml-4">7AM - 2PM</span></div>
                      <div className="flex justify-between"><span>TUESDAY</span><span className="ml-4">7AM - 2PM</span></div>
                      <div className="flex justify-between"><span>WEDNESDAY</span><span className="ml-4">7AM - 2PM</span></div>
                      <div className="flex justify-between"><span>THURSDAY</span><span className="ml-4">7AM - 2PM</span></div>
                      <div className="flex justify-between"><span>FRIDAY</span><span className="ml-4">7AM - 2PM</span></div>
                      <div className="flex justify-between"><span>SATURDAY</span><span className="ml-4">7AM - 3PM</span></div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Explore Nearby Locations Section - Only shown if there are nearby locations */}
      {showNearbySection && (
        <div className="bg-white pt-8 pb-16">
          <Container>
            <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3">
              <h2 className="text-2xl font-bold text-brand-navy font-heading">Explore Nearby Locations</h2>
              <Link 
                to="/locations" 
                className="inline-flex items-center text-brand-navy font-medium hover:underline"
              >
                View all locations
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </Link>
            </div>

            {/* Nearby Locations */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {nearbyLocations.length > 0 ? (
                nearbyLocations.map(loc => {
                  // Remove distance property for display
                  const { distance, ...locationWithoutDistance } = loc;
                  return <LocationCard key={loc._id} location={locationWithoutDistance} />;
                })
              ) : (
                // Show skeletons while loading nearby locations
                [...Array(3)].map((_, index) => (
                  <LocationCardSkeleton key={index} />
                ))
              )}
            </div>
          </Container>
        </div>
      )}
    </>
  );
}